import React, { useContext, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputRightAddon,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import { useFormik } from "formik";
import CreatableSelect from "react-select/creatable";
import FormInput from "../../components/form/FormInput";
import { GlobalContext } from "../../context/Provider";
import drugsList from "../../utils/drugsList";
import { getAgeApp } from "../../utils/getAge";
import { MoneyInput } from "./PaymentSection";

const emptyMedication = () => ({
  drug: "",
  dosage: "",
  amount: "",
  discount: 0,
  totalAmount: 0,
});

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
};

const lineTotal = (item) => Math.round(
  Math.max(number(item.amount) - ((number(item.amount) * number(item.discount)) / 100), 0) * 100,
) / 100;

const currency = (value) => new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  minimumFractionDigits: 2,
}).format(number(value));

const Prescription = ({ form, onSubmitAttachment, bioData, onPaymentChange }) => {
  const { authState } = useContext(GlobalContext);
  const formik = useFormik({
    initialValues: form || {
      patientAge: bioData?.dateOfBirth ? getAgeApp(bioData.dateOfBirth) : "",
      patientSex: bioData?.sex || "",
      patientName: `${bioData?.surname || ""} ${bioData?.otherNames || ""}`.trim(),
      date: new Date().toLocaleDateString(),
      patientAddress: bioData?.homeAddress || "",
      patientNHIS: bioData?.NHISNo || "",
      patientTel: bioData?.telephone || "",
      patientHMONo: bioData?.name || "",
      prescribersName: `${authState.user?.firstName || ""} ${authState.user?.lastName || ""}`.trim(),
      prescriptionItems: [emptyMedication()],
    },
    validate: (values) => {
      const itemErrors = (values.prescriptionItems || []).map((item) => ({
        ...(!String(item.drug || "").trim() ? { drug: "Drug is required" } : {}),
        ...(!String(item.dosage || "").trim() ? { dosage: "Dosage is required" } : {}),
        ...(item.amount === "" || item.amount == null ? { amount: "Amount is required" } : {}),
        ...(number(item.discount) > 100 ? { discount: "Use a percentage from 0 to 100" } : {}),
      }));
      return itemErrors.some((item) => Object.keys(item).length)
        ? { prescriptionItems: itemErrors }
        : {};
    },
    onSubmit: (values) => onSubmitAttachment({
      attachmentType: "prescription",
      attachment: {
        ...values,
        prescriptionItems: values.prescriptionItems.map((item) => ({
          ...item,
          amount: number(item.amount),
          discount: number(item.discount),
          totalAmount: lineTotal(item),
        })),
      },
    }),
  });

  const items = formik.values.prescriptionItems || [];
  useEffect(() => {
    const totals = items.reduce((result, item) => ({
      fullAmount: result.fullAmount + number(item.amount),
      totalAmount: result.totalAmount + lineTotal(item),
    }), { fullAmount: 0, totalAmount: 0 });
    onPaymentChange?.((current) => ({
      ...current,
      fullAmount: Math.round(totals.fullAmount * 100) / 100,
      discount: 0,
      discountType: "percentage",
      totalAmount: Math.round(totals.totalAmount * 100) / 100,
    }));
  }, [items, onPaymentChange]);

  const updateItem = (index, field, value) => {
    const next = items.map((item, itemIndex) => itemIndex === index
      ? { ...item, [field]: value }
      : item);
    formik.setFieldValue("prescriptionItems", next);
  };

  return (
    <Box p={{ base: "2", md: "3" }} w="full">
      <form onSubmit={formik.handleSubmit}>
        <Text textAlign="center" fontSize="25px" fontWeight="700">Prescription Form</Text>
        <SimpleGrid columns={{ base: 1, md: 2 }} my="5" spacing="4">
          <FormInput type="text" onChange={formik.handleChange("patientName")} value={formik.values.patientName} label="Name" />
          <FormInput type="text" onChange={formik.handleChange("patientAge")} value={formik.values.patientAge} label="Age" />
          <FormInput type="text" onChange={formik.handleChange("patientSex")} value={formik.values.patientSex} label="Sex" />
          <FormInput type="text" onChange={formik.handleChange("date")} value={formik.values.date} label="Date" />
          <FormInput type="text" onChange={formik.handleChange("patientAddress")} value={formik.values.patientAddress} label="Address" />
          <FormInput type="text" onChange={formik.handleChange("patientNHIS")} value={formik.values.patientNHIS} label="NHIS No" />
          <FormInput type="text" onChange={formik.handleChange("patientHMONo")} value={formik.values.patientHMONo} label="HMO No" />
          <FormInput type="text" onChange={formik.handleChange("patientTel")} value={formik.values.patientTel} label="Telephone" />
          <FormInput type="text" onChange={formik.handleChange("prescribersName")} value={formik.values.prescribersName} label="Prescriber's name" />
        </SimpleGrid>

        <HStack justify="space-between" align="flex-start" mb="3">
          <Box>
            <Heading size="sm">Medications</Heading>
            <Text mt="1" fontSize="sm" color="gray.500">Select a listed drug or type a new one, then enter its dosage and pricing.</Text>
          </Box>
          <Button type="button" size="sm" leftIcon={<AddIcon />} onClick={() => formik.setFieldValue("prescriptionItems", [...items, emptyMedication()])}>
            Add drug
          </Button>
        </HStack>

        <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
          <Grid display={{ base: "none", lg: "grid" }} templateColumns="2fr 1.5fr 1fr .8fr 1fr 40px" gap="3" px="4" py="3" bg="gray.50">
            {['Drug', 'Dosage', 'Amount', 'Discount', 'Total amount', ''].map((label) => <Text key={label} fontSize="xs" fontWeight="800" color="gray.600" textTransform="uppercase">{label}</Text>)}
          </Grid>
          {items.map((item, index) => {
            const errors = formik.touched.prescriptionItems?.[index] ? formik.errors.prescriptionItems?.[index] || {} : {};
            return (
              <Grid key={index} templateColumns={{ base: "1fr", lg: "2fr 1.5fr 1fr .8fr 1fr 40px" }} gap="3" p="4" borderTopWidth={index > 0 ? "1px" : "0"} alignItems="start">
                <FormControl isInvalid={Boolean(errors.drug)}>
                  <FormLabel display={{ lg: "none" }}>Drug</FormLabel>
                  <CreatableSelect
                    isClearable
                    options={drugsList}
                    value={item.drug ? { label: item.drug, value: item.drug } : null}
                    onChange={(option) => updateItem(index, "drug", option?.value || "")}
                    placeholder="Select or enter drug"
                    styles={{ control: (base) => ({ ...base, minHeight: "40px" }), menuPortal: (base) => ({ ...base, zIndex: 1600 }) }}
                    menuPortalTarget={document.body}
                  />
                  <FormErrorMessage>{errors.drug}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.dosage)}>
                  <FormLabel display={{ lg: "none" }}>Dosage</FormLabel>
                  <Input value={item.dosage} onChange={(event) => updateItem(index, "dosage", event.target.value)} placeholder="e.g. 1 tablet twice daily" />
                  <FormErrorMessage>{errors.dosage}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.amount)}>
                  <FormLabel display={{ lg: "none" }}>Amount</FormLabel>
                  <MoneyInput value={item.amount} onChange={(value) => updateItem(index, "amount", value)} />
                  <FormErrorMessage>{errors.amount}</FormErrorMessage>
                </FormControl>
                <FormControl isInvalid={Boolean(errors.discount)}>
                  <FormLabel display={{ lg: "none" }}>Discount</FormLabel>
                  <InputGroup>
                    <Input type="number" min="0" max="100" step="0.01" value={item.discount} onChange={(event) => updateItem(index, "discount", event.target.value)} />
                    <InputRightAddon>%</InputRightAddon>
                  </InputGroup>
                  <FormErrorMessage>{errors.discount}</FormErrorMessage>
                </FormControl>
                <GridItem>
                  <FormLabel display={{ lg: "none" }}>Total amount</FormLabel>
                  <Text h="40px" display="flex" alignItems="center" fontWeight="800" color="brand.700">{currency(lineTotal(item))}</Text>
                </GridItem>
                <IconButton type="button" aria-label={`Remove medication ${index + 1}`} icon={<DeleteIcon />} colorScheme="red" variant="ghost" isDisabled={items.length === 1} onClick={() => formik.setFieldValue("prescriptionItems", items.filter((_, itemIndex) => itemIndex !== index))} />
              </Grid>
            );
          })}
        </Box>

        <Button variant="outline" width="full" mt="6" type="submit">
          {formik.isSubmitting ? <CircularProgress isIndeterminate size="24px" color="teal" /> : "Attach"}
        </Button>
      </form>
    </Box>
  );
};

export default Prescription;
